import { Css, GridTable, ScrollableContent, column, simpleHeader } from "@homebound/beam";
import { createDevelopmentProductOfferingUrl, createProjectProductOfferingUrl } from "src/RouteUrls";
import { ProgressPill } from "src/components";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { ProductOfferingsDetailFragment } from "src/routes/developments/product-offerings/ProductOfferingsPage.generated";
import { programDataBathRange, programDataRange } from "src/utils/programData";
import { UpdatesAvailableButton } from "./components/UpdatesAvailable";

export function ProductOfferingsTable({ productOfferings }: { productOfferings: ProductOfferingsDetailFragment[] }) {
  const columns = [
    column<Row>({
      header: "Offering Name",
      productOffering: (po) => {
        return {
          content: (
            <div css={Css.df.aic.gap("4px").$}>
              <div css={Css.smSb.$}>{po.name}</div>
              <span>{po.aggregateLatest.displayVersion}</span>
              <UpdatesAvailableButton
                productOfferingId={po.id}
                updates={po.availableUpdates}
                copyInProgress={po.copyInProgress}
                isArchived={po.status.code === ReadyPlanStatus.Archived}
                showText={false}
              />
            </div>
          ),
          value: po.name,
        };
      },
    }),
    column<Row>({ header: "Plan Code", productOffering: (po) => po.planPackage.code }),
    column<Row>({
      header: "Sqft",
      productOffering: ({ minProgramData, maxProgramData }) =>
        programDataRange(minProgramData, maxProgramData, "sellableSqft"),
    }),
    column<Row>({
      header: "Beds",
      productOffering: ({ minProgramData, maxProgramData }) =>
        programDataRange(minProgramData, maxProgramData, "bedrooms"),
    }),
    column<Row>({
      header: "Baths",
      productOffering: (po) => programDataBathRange(po.minProgramData, po.maxProgramData),
    }),
    column<Row>({
      header: "Elevations",
      productOffering: (po) => po.options.filter((rpo) => rpo.active && rpo.type.isElevation).length,
    }),
    column<Row>({
      header: "Width",
      productOffering: ({ minProgramData, maxProgramData }) =>
        programDataRange(minProgramData, maxProgramData, "widthInFeet"),
    }),
    column<Row>({
      header: "Depth",
      productOffering: ({ minProgramData, maxProgramData }) =>
        programDataRange(minProgramData, maxProgramData, "depthInFeet"),
    }),
    column<Row>({
      header: "Bid Out",
      productOffering: (po) => <ProgressPill progress={po.bidOutPercentage} stacked changeColorOnCompleted />,
    }),
  ];

  const rows = [
    simpleHeader,
    ...productOfferings.map((po) => ({ kind: "productOffering" as const, data: po, id: po.id })),
  ];
  return (
    <>
      <ScrollableContent>
        <GridTable
          rows={rows}
          columns={columns}
          rowStyles={{
            productOffering: {
              rowLink: ({ data }) =>
                data.development
                  ? createDevelopmentProductOfferingUrl(data.development!.id, data.id, data.aggregateLatest.id)
                  : createProjectProductOfferingUrl(data.project!.id, data.id, data.aggregateLatest.id),
            },
          }}
        />
      </ScrollableContent>
    </>
  );
}

type HeaderRow = { kind: "header"; data: undefined };
type ProductOfferingRow = { kind: "productOffering"; data: ProductOfferingsDetailFragment };
type Row = HeaderRow | ProductOfferingRow;
